// Two factor authentication with an Authenticator app (G-03 admin MFA). Backend: app/api/routes/mfa.py.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/ui'
import { api } from '@/lib/api'
import { t } from '@/lib/i18n'

export type MfaStatus = {
  enabled: boolean
  pending: boolean
  required: boolean
  verified: boolean
  verifiedAt: string | null
  validUntil: string | null
  confirmedAt: string | null
  lastUsedAt: string | null
  recoveryCodesLeft: number
}

export type MfaEnrollment = { secret: string; otpauthUri: string; qrSvg: string; issuer: string; account: string }
export type MfaVerifyInput = { code?: string; recoveryCode?: string }
export type MfaVerifyResult = { method: 'totp' | 'recovery'; verifiedAt: string; validUntil: string; recoveryCodesLeft: number }

export const mfaKeys = { status: ['mfa', 'status'] as const }

export function useMfaStatus(enabled = true) {
  return useQuery({
    queryKey: mfaKeys.status,
    queryFn: ({ signal }) => api.get<MfaStatus>('/api/me/mfa', { signal }),
    enabled,
    staleTime: 30_000,
  })
}

export function useMfaEnroll() {
  return useMutation({ mutationFn: () => api.post<MfaEnrollment>('/api/me/mfa/enroll') })
}

export function useMfaConfirm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ recoveryCodes: string[]; status: MfaStatus }>('/api/me/mfa/enroll/confirm', { code }),
    onSuccess: (out) => qc.setQueryData(mfaKeys.status, out.status),
  })
}

export function useMfaVerify() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MfaVerifyInput) => api.post<MfaVerifyResult>('/api/me/mfa/verify', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mfaKeys.status }),
  })
}

export function useMfaRegenerate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/api/me/mfa/recovery-codes'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mfaKeys.status }),
  })
}

export function useMfaDisable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<{ ok: true; sessionsRevoked: number }>('/api/me/mfa'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mfaKeys.status })
      void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] })
    },
  })
}

export async function copyText(text: string, done: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(done)
  } catch {
    toast.error(t('Could not copy. Select the text and copy it yourself.'))
  }
}

/** Groups a base32 secret in blocks of four so it is easier to type into an app. */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

/** Plain text for the "Download" button: the codes plus where they belong. */
export function recoveryCodesFile(codes: string[], account: string): string {
  return [
    'MG AI School recovery codes',
    account,
    '',
    'Each code works once. Keep them somewhere safe.',
    '',
    ...codes,
    '',
  ].join('\n')
}
